import { useEffect, useMemo, useState } from "react";
import type { User } from "@swift/shared";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { vindOfMaakDm } from "../lib/gesprek";
import { zoekPersonen } from "../lib/personen";

type Result = {
  id: number;
  conversation_id: number;
  sender_id: number;
  body: string;
  created_at: string;
};

/**
 * Zoeken in berichten én in mensen.
 *
 * Mensen zijn erbij gekomen omdat de zijbalk alleen wie online is laat zien.
 * Met een collega die net niet achter zijn scherm zit en waar je nog nooit mee
 * gechat hebt, viel simpelweg geen gesprek te beginnen. Nu typ je zijn naam.
 *
 * Het zoeken naar mensen gaat door de lijst die de app toch al in het geheugen
 * heeft - geen serveraanroep, dus het staat er terwijl je typt, en het werkt
 * ook zonder verbinding. Berichten blijven via de server gaan; daar is een
 * fulltext-index voor.
 */
const MIN_BERICHTEN = 2;

export default function SearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState<number | null>(null);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const setActive = useStore((s) => s.setActiveConversation);

  const personen = useMemo(
    () => zoekPersonen(users, q, me?.id),
    [users, q, me?.id],
  );

  useEffect(() => {
    if (q.trim().length < MIN_BERICHTEN) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { results } = await api.search(q.trim());
        setResults(results);
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [q]);

  async function openPersoon(u: User) {
    setBezig(u.id);
    try {
      setActive(await vindOfMaakDm(u.id));
      setOpen(false);
      setQ("");
    } finally {
      setBezig(null);
    }
  }

  const ietsTeZien = personen.length > 0 || results.length > 0;

  return (
    <div className="relative">
      <input
        className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-200 outline-none focus:border-accent"
        placeholder="Zoek een bericht of collega…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && ietsTeZien && (
        <div className="absolute left-0 right-0 z-10 mt-1 max-h-96 overflow-y-auto rounded border border-slate-700 bg-slate-800 shadow-xl">
          {personen.length > 0 && (
            <>
              <Kop>Mensen</Kop>
              {personen.map((u) => (
                <button
                  key={`u${u.id}`}
                  disabled={bezig === u.id}
                  className="flex w-full items-center gap-2 border-b border-slate-700 px-2 py-2 text-left text-xs last:border-b-0 hover:bg-slate-700 disabled:opacity-50"
                  onMouseDown={(e) => {
                    /* onMouseDown en niet onClick: onBlur van het invoerveld
                       sluit de lijst voordat een click zou aankomen. */
                    e.preventDefault();
                    void openPersoon(u);
                  }}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      u.status_kind === "online"
                        ? "bg-brand-green"
                        : u.status_kind === "away"
                        ? "bg-yellow-500"
                        : u.status_kind === "busy"
                        ? "bg-red-500"
                        : "bg-slate-600"
                    }`}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium text-slate-200">{u.display_name}</span>
                    <span className="ml-1 text-slate-500">@{u.username}</span>
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wider text-slate-500">
                    {bezig === u.id ? "…" : "gesprek"}
                  </span>
                </button>
              ))}
            </>
          )}

          {results.length > 0 && (
            <>
              {personen.length > 0 && <Kop>Berichten</Kop>}
              {results.map((r) => (
                <button
                  key={`m${r.id}`}
                  className="block w-full border-b border-slate-700 px-2 py-2 text-left text-xs text-slate-300 last:border-b-0 hover:bg-slate-700"
                  onMouseDown={() => {
                    setActive(r.conversation_id);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  <div className="font-medium text-slate-200">
                    {users[r.sender_id]?.display_name ?? `Gebruiker #${r.sender_id}`}
                    <span className="ml-2 font-normal text-slate-500">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="line-clamp-2 text-slate-400">{r.body}</div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Kop({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-slate-700 bg-slate-900/60 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">
      {children}
    </div>
  );
}
